<template>
  <div v-if="attachments && attachments.length > 0" class="comment-attachments-list">
    <AlertBanner
      v-if="downloadError"
      type="error"
      :title="s__('Attachments|Download Failed')"
      :message="downloadError"
      :dismissible="true"
      :duration="0"
      @dismiss="downloadError = null"
    />
    <AlertBanner
      v-if="sendToTelegramInfo"
      type="info"
      :title="s__('Attachments|Sending to Telegram')"
      :message="
        s__(
          'Attachments|Your attachment is being sent to your Telegram Direct Messages. This may take a few moments.'
        )
      "
      :dismissible="true"
      :duration="5000"
      @dismiss="sendToTelegramInfo = false"
    />
    <AlertBanner
      v-if="sendToTelegramError"
      type="error"
      :title="s__('Attachments|Send to Telegram Failed')"
      :message="sendToTelegramError"
      :dismissible="true"
      :duration="0"
      @dismiss="sendToTelegramError = null"
    />
    <div v-for="attachment in attachments" :key="attachment.id" class="comment-attachment-item">
      <div
        class="comment-attachment-icon"
        :class="getFileIconColor(attachment.contentType, attachment.filename)"
      >
        <component :is="getFileIcon(attachment)" />
      </div>
      <div class="comment-attachment-info">
        <span class="comment-attachment-filename" :title="attachment.filename">
          {{ attachment.filename }}
        </span>
        <span class="comment-attachment-size">{{ attachment.humanSize }}</span>
      </div>
      <div class="comment-attachment-actions">
        <button
          v-if="isImageAttachment(attachment)"
          type="button"
          class="comment-attachment-btn comment-attachment-view"
          :title="s__('Attachments|View image')"
          @click="openImageGallery(attachment)"
        >
          <EyeIcon :size="14" />
        </button>
        <button
          type="button"
          class="comment-attachment-btn comment-attachment-download"
          :title="s__('Attachments|Download')"
          :disabled="downloading"
          @click="handleDownload(attachment)"
        >
          <DownloadIcon :size="14" />
        </button>
        <AttachmentSendToTelegramButton
          button-class="comment-attachment-btn comment-attachment-telegram"
          :icon-size="14"
          :disabled="sendingToTelegram"
          @send="handleSendToTelegram(attachment)"
        />
        <AttachmentPublicLinkButton
          :attachment="attachment"
          button-class="comment-attachment-btn comment-attachment-share"
          :icon-size="14"
        />
        <button
          v-if="canDelete"
          class="comment-attachment-btn comment-attachment-delete"
          @click="handleDelete(attachment)"
          :title="s__('Attachments|Delete attachment')"
        >
          <TrashIcon :size="14" color="#dc2626" />
        </button>
      </div>
    </div>
  </div>

  <!-- Image Gallery Modal -->
  <ImageGalleryModal
    :visible="showImageGallery"
    :images="galleryImages"
    :initial-index="galleryInitialIndex"
    @close="closeImageGallery"
  />
</template>

<script setup>
import { computed, ref } from 'vue'
import { s__ } from '@/locale'
import AlertBanner from './AlertBanner.vue'
import ImageGalleryModal from './ImageGalleryModal.vue'
import FileIcon from './icons/FileIcon.vue'
import ImageFileIcon from './icons/ImageFileIcon.vue'
import VideoFileIcon from './icons/VideoFileIcon.vue'
import AudioFileIcon from './icons/AudioFileIcon.vue'
import ArchiveFileIcon from './icons/ArchiveFileIcon.vue'
import DocumentFileIcon from './icons/DocumentFileIcon.vue'
import TextFileIcon from './icons/TextFileIcon.vue'
import DownloadIcon from './icons/DownloadIcon.vue'
import TrashIcon from './icons/TrashIcon.vue'
import EyeIcon from './icons/EyeIcon.vue'
import AttachmentSendToTelegramButton from './AttachmentSendToTelegramButton.vue'
import AttachmentPublicLinkButton from './AttachmentPublicLinkButton.vue'
import { useAttachmentDownload } from '@/composables/useAttachmentDownload'
import { useMutation } from '@vue/apollo-composable'
import gql from 'graphql-tag'

const SEND_TO_TELEGRAM_MUTATION = gql`
  mutation SendAttachmentToTelegram($id: ID!) {
    sendAttachmentToTelegram(id: $id) {
      success
      errors
    }
  }
`

const props = defineProps({
  attachments: {
    type: Array,
    default: () => [],
  },
  canDelete: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['delete'])

// Download composable for iOS support
const { downloading, downloadError, downloadAttachment } = useAttachmentDownload()

// Send to Telegram state
const sendingToTelegram = ref(false)
const sendToTelegramError = ref(null)
const sendToTelegramInfo = ref(false)
const { mutate: sendToTelegramMutation } = useMutation(SEND_TO_TELEGRAM_MUTATION)

// Image gallery state
const showImageGallery = ref(false)
const galleryImages = ref([])
const galleryInitialIndex = ref(0)

const handleDownload = async attachment => {
  await downloadAttachment(attachment.downloadUrl, attachment.filename)
}

const handleSendToTelegram = async attachment => {
  if (!attachment) return

  sendingToTelegram.value = true
  sendToTelegramError.value = null
  sendToTelegramInfo.value = false

  try {
    const { data } = await sendToTelegramMutation({ id: attachment.id })

    if (data?.sendAttachmentToTelegram?.errors?.length) {
      // Error occurred (rate limit, missing file, authorization, etc.) - show error immediately
      const errorMsg = data.sendAttachmentToTelegram.errors.join(', ')
      sendToTelegramError.value = s__('Attachments|Failed to send to Telegram: %{error}').replace(
        '%{error}',
        errorMsg
      )
      sendToTelegramInfo.value = false
    } else if (data?.sendAttachmentToTelegram?.success) {
      // Job was successfully enqueued - show info banner
      sendToTelegramInfo.value = true
      // Hide info banner after a short delay
      setTimeout(() => {
        sendToTelegramInfo.value = false
      }, 5000)
    }
  } catch (error) {
    console.error('Send to Telegram error:', error)
    sendToTelegramError.value = s__('Attachments|Failed to send to Telegram')
    sendToTelegramInfo.value = false
  } finally {
    sendingToTelegram.value = false
  }
}

const getFileIcon = attachment => {
  const contentType = attachment.contentType || ''
  const filename = attachment.filename || ''

  // Check by content type first
  if (contentType.startsWith('image/')) {
    return ImageFileIcon
  }
  if (contentType.startsWith('video/')) {
    return VideoFileIcon
  }
  if (contentType.startsWith('audio/')) {
    return AudioFileIcon
  }
  if (contentType === 'application/pdf') {
    return DocumentFileIcon
  }
  if (contentType === 'application/epub+zip' || contentType.includes('epub')) {
    return DocumentFileIcon
  }
  if (
    contentType === 'application/msword' ||
    contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    contentType.includes('word') ||
    contentType.includes('document')
  ) {
    return DocumentFileIcon
  }
  if (
    contentType === 'application/zip' ||
    contentType === 'application/x-rar-compressed' ||
    contentType.includes('zip') ||
    contentType.includes('rar') ||
    contentType.includes('7z') ||
    contentType.includes('tar') ||
    contentType.includes('gzip')
  ) {
    return ArchiveFileIcon
  }
  if (contentType.startsWith('text/')) {
    return TextFileIcon
  }

  // Fallback to filename extension
  if (filename) {
    const ext = filename.split('.').pop()?.toLowerCase()
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return ImageFileIcon
    if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) return VideoFileIcon
    if (['mp3', 'ogg', 'wav', 'm4a', 'flac'].includes(ext)) return AudioFileIcon
    if (['pdf', 'epub'].includes(ext)) return DocumentFileIcon
    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) return DocumentFileIcon
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return ArchiveFileIcon
    if (['txt', 'csv', 'md'].includes(ext)) return TextFileIcon
  }

  return FileIcon
}

const getFileIconColor = (contentType, filename) => {
  // Check by content type first
  if (contentType) {
    if (contentType.startsWith('image/')) return 'file-icon-color-image'
    if (contentType.startsWith('video/')) return 'file-icon-color-video'
    if (contentType.startsWith('audio/')) return 'file-icon-color-audio'
    if (contentType === 'application/pdf') return 'file-icon-color-pdf'
    if (
      contentType === 'application/msword' ||
      contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      contentType.includes('word') ||
      contentType.includes('document')
    ) {
      return 'file-icon-color-document'
    }
    if (
      contentType === 'application/zip' ||
      contentType === 'application/x-rar-compressed' ||
      contentType.includes('zip') ||
      contentType.includes('rar') ||
      contentType.includes('7z') ||
      contentType.includes('tar') ||
      contentType.includes('gzip')
    ) {
      return 'file-icon-color-archive'
    }
    if (contentType.startsWith('text/')) return 'file-icon-color-text'
  }

  // Fallback to filename extension
  if (filename) {
    const ext = filename.split('.').pop()?.toLowerCase()
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'file-icon-color-image'
    if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) return 'file-icon-color-video'
    if (['mp3', 'ogg', 'wav', 'm4a', 'flac'].includes(ext)) return 'file-icon-color-audio'
    if (['pdf'].includes(ext)) return 'file-icon-color-pdf'
    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext))
      return 'file-icon-color-document'
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return 'file-icon-color-archive'
    if (['txt', 'csv', 'md'].includes(ext)) return 'file-icon-color-text'
  }

  return 'file-icon-color-default'
}

const isImageAttachment = attachment => {
  const contentType = attachment.contentType || ''
  const filename = attachment.filename || ''

  // Check by content type first
  if (contentType.startsWith('image/')) {
    return true
  }

  // Fallback to filename extension
  if (filename) {
    const ext = filename.split('.').pop()?.toLowerCase()
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)
  }

  return false
}

const openImageGallery = attachment => {
  // Filter to only image attachments from this comment
  const imageAttachments = props.attachments.filter(isImageAttachment)
  const imageIndex = imageAttachments.findIndex(img => img.id === attachment.id)

  if (imageIndex !== -1) {
    galleryImages.value = imageAttachments
    galleryInitialIndex.value = imageIndex
    showImageGallery.value = true
  }
}

const closeImageGallery = () => {
  showImageGallery.value = false
  galleryImages.value = []
  galleryInitialIndex.value = 0
}

const handleDelete = async attachment => {
  if (!confirm(s__('Attachments|Are you sure you want to delete this attachment?'))) {
    return
  }

  emit('delete', attachment)
}
</script>

<style scoped>
.comment-attachments-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.comment-attachment-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  transition: background-color 0.2s;
  position: relative;
}

.comment-attachment-item:hover {
  background-color: #f9fafb;
}

.comment-attachment-info {
  flex: 1;
  min-width: 0;
}

.comment-attachment-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
}

.comment-attachment-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.comment-attachment-icon svg {
  width: 20px;
  height: 20px;
  color: inherit;
}

/* File icon colors */
.file-icon-color-image {
  color: #16a34a; /* green-600 */
}

.file-icon-color-video {
  color: #9333ea; /* purple-600 */
}

.file-icon-color-audio {
  color: #ec4899; /* pink-600 */
}

.file-icon-color-pdf {
  color: #dc2626; /* red-600 */
}

.file-icon-color-document {
  color: #dc2626; /* red-600 */
}

.file-icon-color-archive {
  color: #ea580c; /* orange-600 */
}

.file-icon-color-text {
  color: #0891b2; /* cyan-600 */
}

.file-icon-color-default {
  color: #4b5563; /* gray-600 */
}

.comment-attachment-filename {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #111827;
}

.comment-attachment-size {
  display: block;
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.125rem;
}

.comment-attachment-btn {
  padding: 0.25rem;
  border-radius: 0.25rem;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
  transition: background-color 0.2s;
  color: #4b5563;
  text-decoration: none;
  flex-shrink: 0;
}

.comment-attachment-btn:hover:not(:disabled) {
  background-color: rgba(42, 171, 238, 0.1);
  color: #2aabee;
}

:deep(.comment-attachment-share) {
  padding: 0.25rem;
  border-radius: 0.25rem;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
  transition: background-color 0.2s;
  color: #4b5563;
  text-decoration: none;
  flex-shrink: 0;
}

:deep(.comment-attachment-share:hover:not(:disabled)) {
  background-color: rgba(42, 171, 238, 0.1);
  color: #2aabee;
}

:deep(.comment-attachment-share:disabled) {
  opacity: 0.5;
  cursor: not-allowed;
}

.comment-attachment-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.comment-attachment-delete:hover {
  background-color: rgba(220, 38, 38, 0.1);
  color: #dc2626;
}

@media (max-width: 640px) {
  .comment-attachment-item {
    align-items: flex-start;
  }

  .comment-attachment-filename {
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
}
</style>
